import { useState, useEffect, useRef } from "react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import {
  Search,
  DarkMode,
  LightMode,
  Notifications,
  Help,
  Menu,
  Close,
  Settings,
  Home,
  LocalLibrary,
  BarChart,
  MenuBook,
  School,
  AdminPanelSettings,
  AutoAwesome,
  Group,
  Person,
  Celebration,
  Forum,
  Tour,
  Star,
  Public,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import UserImage from "../../components/UserImage";
import { useAuth } from "../../contexts/AuthContext";
import TourDialog from "../widgets/TourDialog";
import { setMode } from "../../state/themeSlice";
import MobileBottomNav from "../../components/MobileBottomNav";

const Navbar = () => {
  const [tourOpen, setTourOpen] = useState(false);
  const [isMobileMenuToggled, setIsMobileMenuToggled] = useState(false);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.theme.mode);
  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");

  // Close the mobile menu when tapping anywhere outside of it.
  // Note: in React 18 the effect below runs before the opening click finishes
  // propagating, so we must ignore the first click the listener receives
  // (that's the one that opened the menu).
  useEffect(() => {
    if (!isMobileMenuToggled) return;
    let skipFirst = true;
    const handleClickOutside = (event) => {
      if (skipFirst) {
        skipFirst = false;
        return;
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuToggled(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMobileMenuToggled]);

  // Helper function to check if a path is active
  const isActive = (path) => {
    if (path === "/home" && location.pathname === "/") return true;
    return location.pathname.startsWith(path);
  };

  const theme = useTheme();
  const neutralLight = theme.palette.neutral.light;
  const dark = theme.palette.neutral.dark;
  const background = theme.palette.background.default;
  const primaryLight = theme.palette.primary.light;
  const alt = theme.palette.background.alt;

  const fullName = user?.profile
    ? `${user.profile.first_name || ""} ${user.profile.last_name || ""}`.trim() ||
      ""
    : "";

  const notificationCount = user?.profile?.notifications?.filter(n => !n.read).length || 0;

  const toggleTheme = () => {
    dispatch(setMode());
  };

  const page = location.pathname;

  return (
    <FlexBetween
      padding={isNonMobileScreens ? "1rem 6%" : "1rem 4%"}
      backgroundColor={alt}
      sx={{
        borderBottom: isNonMobileScreens ? 'none' : `1px solid ${neutralLight}50`,
        boxShadow: isNonMobileScreens ? 'none' : '0 1px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      <FlexBetween gap="1.75rem">
        {isNonMobileScreens ? (
          <Typography
            fontWeight="bold"
            fontSize="clamp(1rem, 2rem, 2.25rem)"
            onClick={() => navigate("/home")}
            sx={{
              color: dark,
              cursor: "pointer",
            }}
          >
            Uben
          </Typography>
        ) : (
          <Typography
            fontWeight="bold"
            fontSize="1.5rem"
            onClick={() => navigate("/home")}
            sx={{
              color: dark,
              cursor: "pointer",
              ml: "0.5rem",
            }}
          >
            Ü
          </Typography>
        )}
      </FlexBetween>

      {/* DESKTOP NAV */}
      {isNonMobileScreens ? (
        <FlexBetween gap="2rem">
          <IconButton onClick={() => navigate("/explore")}>
            <Search sx={{ fontSize: "25px" }} />
          </IconButton>
          <IconButton onClick={() => navigate("/learn")}>
            <School sx={{ fontSize: "25px" }} />
          </IconButton>
          <IconButton onClick={() => navigate("/marketplace")}>
            <Public sx={{ fontSize: "25px" }} />
          </IconButton>
          <IconButton onClick={() => setTourOpen(true)}>
            <Tour sx={{ fontSize: "25px" }} />
          </IconButton>
          
          <IconButton onClick={() => navigate("/notifications")}>
            <Badge badgeContent={notificationCount} color="error" variant="dot" overlap="circular">
              <Notifications sx={{ fontSize: "25px" }} />
            </Badge>
          </IconButton>
          
          <IconButton onClick={toggleTheme}>
            {mode === "dark" ? <DarkMode sx={{ fontSize: "25px" }} /> : <LightMode sx={{ fontSize: "25px" }} />}
          </IconButton>

          <FormControl variant="standard" value={fullName}>
            <Select
              value={fullName}
              sx={{
                backgroundColor: neutralLight,
                width: "150px",
                borderRadius: "0.25rem",
                p: "0.25rem 1rem",
                ml: "1rem",
                "& .MuiSvgIcon-root": {
                  pr: "0.25rem",
                  width: "3rem",
                },
                "& .MuiSelect-select:focus": {
                  backgroundColor: neutralLight,
                },
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight: "32px !important", // Ensure space for dropdown arrow
                },
              }}
              input={<InputBase />}
            >
              <MenuItem value={fullName}>
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {fullName}
                </Typography>
              </MenuItem>
              <MenuItem onClick={() => signOut()}>Log Out</MenuItem>
            </Select>
          </FormControl>
        </FlexBetween>
      ) : (
        <IconButton
          size="small"
          onClick={() => setIsMobileMenuToggled(!isMobileMenuToggled)}
          sx={{
            padding: "8px",
            borderRadius: "10px",
            backgroundColor: isMobileMenuToggled ? `${primaryLight}20` : 'transparent',
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: `${neutralLight}80`,
            },
          }}
        >
          {isMobileMenuToggled ? (
            <Close fontSize="small" sx={{ color: dark }} />
          ) : (
            <Menu fontSize="small" sx={{ color: dark }} />
          )}
        </IconButton>
      )}

      <TourDialog open={tourOpen} onClose={() => setTourOpen(false)} />

      {/* Mobile menu backdrop */}
      {!isNonMobileScreens && isMobileMenuToggled && (
        <Box
          position="fixed"
          inset="0"
          zIndex="1101"
          sx={{
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
          onClick={() => setIsMobileMenuToggled(false)}
        />
      )}

      {/* MOBILE NAV */}
      {!isNonMobileScreens && isMobileMenuToggled && (
        <Box
          ref={mobileMenuRef}
          position="fixed"
          right="0"
          top="0"
          height="100%"
          zIndex="1102"
          maxWidth="350px"
          minWidth="280px"
          boxShadow="-8px 0px 32px rgba(0, 0, 0, 0.15)"
          overflow="auto"
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "& .MuiSvgIcon-root": {
              color: dark,
            },
            transform: isMobileMenuToggled
              ? "translateX(0)"
              : "translateX(100%)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            paddingBottom: "80px",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            backgroundColor: `${background}F2`,
            borderLeft: `1px solid ${neutralLight}40`,
            willChange: 'backdrop-filter',
          }}
        >
          {/* Mobile Menu Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p="1rem"
            borderBottom={`1px solid ${neutralLight}30`}
          >
            <Typography variant="h6" fontWeight="600" color={dark}>
              Menu
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsMobileMenuToggled(!isMobileMenuToggled)}
              sx={{
                borderRadius: "10px",
                padding: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: `${neutralLight}80`,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* User Profile Section */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            p="1.5rem 1rem"
            borderBottom={`1px solid ${neutralLight}`}
          >
            {/* Using UserImage component with proper image handling */}
            <Box
              mb="0.75rem"
              onClick={() => {
                navigate(`/profile/${user.id}`);
                setIsMobileMenuToggled(false);
              }}
              sx={{
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: "50%",
                padding: "2px",
                "&:hover": {
                  transform: "scale(1.1) rotate(5deg)",
                },
              }}
            >
              {user.verified ? (
                <UserImage
                  image={user?.profile?.picture_url}
                  size="55px"
                  verified
                />
              ) : (
                <UserImage image={user?.profile?.picture_url} size="55px" />
              )}
            </Box>
            <Typography
              fontWeight="500"
              fontSize="1rem"
              mb="0.75rem"
              color={dark}
              onClick={() => {
                navigate(`/profile/${user.id}`);
                setIsMobileMenuToggled(false);
              }}
              sx={{
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  color: theme.palette.primary.main,
                  transform: "translateY(-2px)",
                },
                textAlign: "center",
              }}
            >
              {fullName}
            </Typography>
            <Box
              onClick={() => signOut()}
              p="0.5rem 1.5rem"
              borderRadius="25px"
              backgroundColor={neutralLight}
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                border: `1px solid ${neutralLight}`,
                "&:hover": {
                  backgroundColor: theme.palette.error.light,
                  color: "white",
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: `0 6px 20px ${theme.palette.error.main}30`,
                  borderColor: theme.palette.error.main,
                },
              }}
            >
              <Typography fontSize="0.85rem" fontWeight="500">
                Log Out
              </Typography>
            </Box>
          </Box>

          {/* Explore Menu */}
          <Box p="0.75rem">
            {/* ÜBEN LEARNING Section - Secondary items */}
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight="500"
                mb="1rem"
                pl="0.75rem"
                fontSize="0.85rem"
              >
                ÜBEN LEARNING
              </Typography>
<FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/decks");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/decks")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/decks")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <LocalLibrary
                      sx={{
                        fontSize: "20px",
                        color: isActive("/decks")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/decks")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Study Decks
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* Courses */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/courses");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/courses")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/courses")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <MenuBook
                      sx={{
                        fontSize: "20px",
                        color: isActive("/courses")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/courses")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Courses
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* Learn & Resources */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/learn");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/learn")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/learn")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <School
                      sx={{
                        fontSize: "20px",
                        color: isActive("/learn")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/learn")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Learn & Resources
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* Study Statistics */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/stats");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/stats")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/stats")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <BarChart
                      sx={{
                        fontSize: "20px",
                        color: isActive("/stats")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/stats")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Study Statistics
                  </Typography>
                </FlexBetween>
              </FlexBetween>
            </Box>

            {/* Community Section - Secondary items */}
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight="500"
                mb="1rem"
                pl="0.75rem"
                fontSize="0.85rem"
              >
                COMMUNITY
              </Typography>
              {/* Forums */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/forum");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/forum")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/forum")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Forum
                    sx={{
                      fontSize: "20px",
                      color: isActive("/forum")
                        ? theme.palette.primary.main
                        : dark,
                      filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/forum")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Forums
                  </Typography>
                </FlexBetween>
              </FlexBetween>

              {/* Leaderboards */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/leaderboards");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/leaderboards")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/leaderboards")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Celebration
                      sx={{
                        fontSize: "20px",
                        color: isActive("/leaderboards")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/leaderboards")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Leaderboards
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* Community */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/community");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/community")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/community")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Group
                      sx={{
                        fontSize: "20px",
                        color: isActive("/community")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/community")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Community
                  </Typography>
                </FlexBetween>
              </FlexBetween>
            </Box>
            {/* MAIN NAVIGATION Section - Primary items */}
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight="500"
                mb="1rem"
                pl="0.75rem"
                fontSize="0.85rem"
              >
                Explore
              </Typography>

              {/* Explore */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/explore");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/explore")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/explore")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Search
                      sx={{
                        fontSize: "20px",
                        color: isActive("/explore")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/explore")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Explore Decks
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* Marketplace */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/marketplace");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/marketplace")
                    ? theme.palette.primary.main + "15"
                    : "transparent",
                  border: isActive("/marketplace")
                    ? `1px solid ${theme.palette.primary.main}30`
                    : "1px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main + "10",
                    border: `1px solid ${theme.palette.primary.main}20`,
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Box
                    sx={{
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Public
                      sx={{
                        fontSize: "20px",
                        color: isActive("/marketplace")
                          ? theme.palette.primary.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </Box>
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/marketplace")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Community Decks
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              {/* KAnki */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/kanki-community");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/kanki-community")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/kanki-community")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <AutoAwesome
                    sx={{
                      fontSize: "20px",
                      color: isActive("/kanki-community")
                        ? theme.palette.primary.main
                        : dark,
                      filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/kanki-community")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    KAnki
                  </Typography>
                </FlexBetween>
              </FlexBetween>
            </Box>
            {/* Account Section */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight="500"
                mb="1rem"
                pl="0.75rem"
                fontSize="0.85rem"
              >
                ACCOUNT
              </Typography>

              {/* Admin Panel - Only show for admin users */}
              {user?.profile?.is_admin && (
                <FlexBetween
                  p="0.75rem 0.75rem"
                  mb="0.5rem"
                  borderRadius="12px"
                  onClick={() => {
                    navigate("/admin");
                    setIsMobileMenuToggled(false);
                  }}
                  sx={{
                    backgroundColor: isActive("/admin")
                      ? `${theme.palette.warning.light}15`
                      : "transparent",
                    border: isActive("/admin")
                      ? `1px solid ${theme.palette.warning.light}30`
                      : "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: `linear-gradient(90deg, transparent, ${theme.palette.warning.light}10, transparent)`,
                      transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    "&:hover": {
                      backgroundColor: `${theme.palette.warning.light}10`,
                      transform: "translateX(4px)",
                      boxShadow: `0 4px 12px ${theme.palette.warning.light}20`,
                      "&::before": {
                        left: "100%",
                      },
                    },
                  }}
                >
                  <FlexBetween gap="0.875rem">
                    <AdminPanelSettings
                      sx={{
                        fontSize: "20px",
                        color: isActive("/admin")
                          ? theme.palette.warning.main
                          : dark,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                    <Typography
                      fontWeight="500"
                      fontSize="0.95rem"
                      sx={{
                        color: isActive("/admin")
                          ? theme.palette.warning.main
                          : dark,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      Admin Panel
                    </Typography>
                  </FlexBetween>
                </FlexBetween>
              )}

              {/* Settings */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate(`/profile/${user.id}/settings`);
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: location.pathname.includes('/settings')
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: location.pathname.includes('/settings')
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Settings
                    sx={{
                      fontSize: "20px",
                      color: location.pathname.includes('/settings')
                        ? theme.palette.primary.main
                        : dark,
                      filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: location.pathname.includes('/settings')
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Settings
                  </Typography>
                </FlexBetween>
              </FlexBetween>

              {/* Notifications */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/notifications");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/notifications")
                    ? `${theme.palette.primary.light}15`
                    : "transparent",
                  border: isActive("/notifications")
                    ? `1px solid ${theme.palette.primary.light}30`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}10, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light}10`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}20`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Notifications
                    sx={{
                      fontSize: "20px",
                      color: isActive("/notifications")
                        ? theme.palette.primary.main
                        : dark,
                      filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/notifications")
                        ? theme.palette.primary.main
                        : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Notifications
                  </Typography>
                </FlexBetween>
              </FlexBetween>

              {/* Theme Toggle */}
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={toggleTheme}
                sx={{
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, ${neutralLight}20, transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: `${neutralLight}15`,
                    transform: "translateX(4px)",
                    boxShadow: `0 4px 12px ${neutralLight}30`,
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  {mode === "dark" ? (
                    <DarkMode
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.warning.main,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  ) : (
                    <LightMode
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.warning.main,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  )}
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: theme.palette.warning.main,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {mode === "dark" ? "Light" : "Dark"} Mode
                  </Typography>
                </FlexBetween>
              </FlexBetween>

              {/* Go Pro / Pro Member */}
              {user?.profile?.plan === 'pro' || user?.profile?.is_admin === true ? (
                <FlexBetween
                  p="0.75rem 0.75rem"
                  mb="0.5rem"
                  borderRadius="12px"
                  sx={{
                    backgroundColor: 'rgba(245,158,11,0.15)',
                    border: '1px solid rgba(245,158,11,0.3)',
                    cursor: 'default',
                  }}
                >
                  <FlexBetween gap="0.875rem">
                    <Star
                      sx={{
                        fontSize: "20px",
                        color: '#f59e0b',
                      }}
                    />
                    <Typography fontWeight="500" fontSize="0.95rem" sx={{ color: '#f59e0b' }}>
                      Pro Member
                    </Typography>
                  </FlexBetween>
                </FlexBetween>
              ) : (
              <FlexBetween
                p="0.75rem 0.75rem"
                mb="0.5rem"
                borderRadius="12px"
                onClick={() => {
                  navigate("/pricing");
                  setIsMobileMenuToggled(false);
                }}
                sx={{
                  backgroundColor: isActive("/pricing")
                    ? 'rgba(245,158,11,0.15)'
                    : "transparent",
                  border: isActive("/pricing")
                    ? '1px solid rgba(245,158,11,0.3)'
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(90deg, transparent, rgba(245,158,11,0.1), transparent)`,
                    transition: "left 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover": {
                    backgroundColor: 'rgba(245,158,11,0.1)',
                    transform: "translateX(4px)",
                    boxShadow: '0 4px 12px rgba(245,158,11,0.2)',
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <FlexBetween gap="0.875rem">
                  <Star
                    sx={{
                      fontSize: "20px",
                      color: isActive("/pricing") ? '#f59e0b' : dark,
                      filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <Typography
                    fontWeight="500"
                    fontSize="0.95rem"
                    sx={{
                      color: isActive("/pricing") ? '#f59e0b' : dark,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Go Pro
                  </Typography>
                </FlexBetween>
              </FlexBetween>
              )}
            </Box>
          </Box>
        </Box>
      )}
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </FlexBetween>
  );
};

export default React.memo(Navbar);
